import { useEffect, Dispatch, SetStateAction } from "react";
import styled from "styled-components/macro";

import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import FeedbackIcon from "@mui/icons-material/Feedback";
import ListItemText from "@mui/material/ListItemText";
import UpdateIcon from "@mui/icons-material/Update";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SettingsIcon from "@mui/icons-material/Settings";
import { Link } from "react-router-dom";

import { useMediaQuery } from "@mui/material";
import { BREAKPOINT_DESKTOP, getIsIe11 } from "../../../../constants/constants";
import ColorLegend from "../../ColorLegend";
import { ListItemButton } from "../drawerUtils";
import { SideDrawerContentStyles } from "../DrawerStyles";
import {
  useGetPageFromPath,
  useHideOverflow,
  useWorkScheduleIdOnCurrentPage,
} from "../../../../utils/customHooks/genericHooks";
import { useFlightTranslation } from "../../../../utils/customHooks/useFlightTranslation";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { useAuth0 } from "../../../../contexts/AuthManager/Auth";
import { useLongPressTimer } from "utils/customHooks/handleLongPress";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import PrintOrDownloadButton from "./PrintOrDownloadButton";
import { useIsDarkMode } from "store/atoms";
import { getBrowserInfo } from "utils/cssUtils";

const LinkButton = styled.button`
  cursor: pointer;
  text-decoration: underline;
  color: #1a0dab;
  font-family: "Roboto", "Helvetica", "Arial", sans-serif;
  border: none;
  background: none;
  font-size: 1em;
  padding: 0;
  width: 254px;
`;

export const RAVEN_TERMS_URL = "https://raven.ai/terms";
export const RAVEN_PRIVACY_URL = "https://raven.ai/privacy-policy/";

export const TermsAndPrivacy = ({ onLinkClick }) => (
  <Typography color="inherit">
    <LinkButton
      onClick={() => onLinkClick(RAVEN_TERMS_URL)}
      onTouchStart={() => onLinkClick(RAVEN_TERMS_URL)}
    >
      Terms
    </LinkButton>
    {" and "}
    <LinkButton
      onClick={() => onLinkClick(RAVEN_PRIVACY_URL)}
      onTouchStart={() => onLinkClick(RAVEN_PRIVACY_URL)}
    >
      Privacy
    </LinkButton>
  </Typography>
);

type RightDrawerContentProps = {
  setIsRightDrawerOpen: Dispatch<SetStateAction<boolean>>;
  isTest?: boolean;
};

// tslint:disable-next-line: no-big-function
// tslint:disable-next-line cognitive-complexity
export default function RightDrawerContent({
  setIsRightDrawerOpen,
}: RightDrawerContentProps) {
  const ckey = useCustomerKey();
  const { isCustomerTilesPage, isHubletPage } = useGetPageFromPath();

  // prevent 15px layout jump when overflow changes
  const isDesktopOrLarger = useMediaQuery(
    `(min-width: ${BREAKPOINT_DESKTOP}px)`
  );
  const shouldHideOverflow = isHubletPage && isDesktopOrLarger;
  useHideOverflow(shouldHideOverflow);
  useEffect(() => {
    if (!document.body) {
      return;
    }
    document.body.style.paddingRight = "unset";
  }, [isHubletPage, isDesktopOrLarger]);

  const { user, loginWithRedirect, logout } = useAuth0();
  const { translate } = useFlightTranslation();

  const isStorybook = useIsStorybook();
  const shouldShowHelpAndSettings = !isCustomerTilesPage || isStorybook;

  const isIE11 = getIsIe11();

  const browserInfo = getBrowserInfo();

  const handleEmptyCacheAndHardReload = () => {
    // delete caches https://developer.mozilla.org/en-US/docs/Web/API/CacheStorage
    isIE11
      ? (window.location as any).reload(true) // reload(true) is deprecated in newer browsers
      : caches
          .keys()
          .then(function (cacheNames: string[]) {
            return Promise.all(
              cacheNames.map(function (cacheName: string) {
                console.log("Deleting cache:", cacheName);
                return caches.delete(cacheName);
              })
            );
          })
          .then(() => {
            // location.reload(true) doesn't work on chrome/safari https://stackoverflow.com/questions/5721704/window-location-reload-with-clear-cache
            if (
              browserInfo.browser.name === "Chrome" ||
              browserInfo.browser.name === "Safari"
            ) {
              window.location.href = String(window.location.href);
            } else {
              (window.location as any).reload(true);
            }
          });
  };

  const handleUpdateRefresh = (shouldClearCache = false) => {
    const waitingServiceWorkerPromise =
      navigator?.serviceWorker?.getRegistration();

    function reload() {
      if (shouldClearCache) {
        handleEmptyCacheAndHardReload();
      } else {
        window.location.reload();
      }
    }

    // if there's a waiting service worker, tell it to skip waiting THEN reload
    if (waitingServiceWorkerPromise) {
      waitingServiceWorkerPromise
        .then(registration => {
          if (registration?.waiting) {
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
          }
          reload();
        })
        .catch(err => {
          console.log(err);
          reload();
        });
      // otherwise just reload
    } else {
      reload();
    }
  };

  const { onContextMenu, onTouchStart, onMouseDown, onTouchEnd, onMouseUp } =
    useLongPressTimer({
      callback: () => handleUpdateRefresh(true),
      delay: 1500,
    });

  const [isDarkMode] = useIsDarkMode();
  const workScheduleId = useWorkScheduleIdOnCurrentPage();
  return (
    <SideDrawerContentStyles
      $isDarkMode={isDarkMode}
      className="rightDrawer doNotPrint"
      data-component-name={"Right Drawer"}
    >
      <div className="list">
        <div className="userBlock">
          {user && (
            <>
              <Avatar src={user.picture} className="avatar" />
              <Typography
                variant="h6"
                color="inherit"
                align="center"
                className="name"
              >
                {user.name}
              </Typography>
              <Typography
                variant="body1"
                color="inherit"
                align="center"
                className="email"
              >
                {user.email}
              </Typography>
            </>
          )}
        </div>
        <Divider />
        <List>
          <ListItemButton
            onClick={handleUpdateRefresh}
            // long-press to hard reload
            onContextMenu={onContextMenu}
            onTouchStart={onTouchStart}
            onMouseDown={onMouseDown}
            onTouchEnd={onTouchEnd}
            onMouseUp={onMouseUp}
            aria-label="Update / Refresh"
          >
            <ListItemIcon>
              <UpdateIcon />
            </ListItemIcon>
            <ListItemText primary={translate("Update_/_Refresh")} />
          </ListItemButton>
          {shouldShowHelpAndSettings && (
            <Link
              onClick={() => {
                setIsRightDrawerOpen(false);
              }}
              to={`/${ckey}/settings${
                workScheduleId ? `?workSchedule=${workScheduleId}` : ""
              }`}
            >
              <ListItemButton aria-label="Settings">
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                <ListItemText primary={translate("Settings")} />
              </ListItemButton>
            </Link>
          )}
          {shouldShowHelpAndSettings && (
            <Link
              onClick={() => {
                setIsRightDrawerOpen(false);
              }}
              to={`/${ckey}/help`}
            >
              <ListItemButton aria-label="Help and Feedback">
                <ListItemIcon>
                  <FeedbackIcon />
                </ListItemIcon>
                <ListItemText primary={translate("Help_&_Feedback")} />
              </ListItemButton>
            </Link>
          )}
          <PrintOrDownloadButton {...{ setIsRightDrawerOpen }} />
          <ListItemButton
            onClick={() => {
              logout();
              loginWithRedirect({
                appState: {
                  targetUrl: window.location.pathname + window.location.search,
                },
              });
            }}
            aria-label="Sign Out"
          >
            <ListItemIcon>
              <ExitToAppIcon />
            </ListItemIcon>
            <ListItemText primary={translate("Sign_Out")} />
          </ListItemButton>
        </List>
        <Divider />
      </div>

      <div className="bottomBlock">
        <ColorLegend />
        <Divider />
        <Typography className="version" variant="body1" color="inherit">
          Flight Version: {process.env.REACT_APP_VERSION}
        </Typography>
      </div>
    </SideDrawerContentStyles>
  );
}
